import type { ReactNode } from "react";
import { motion } from "framer-motion";

import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { cn } from "@/lib/utils";

/**
 * Wraps content in a pull-to-refresh gesture. Renders a brand-tinted
 * compass indicator above the content that rotates as the user pulls;
 * a full rotation past the threshold arms the refresh. On release,
 * calls `onRefresh` and shows the spinner until it resolves.
 *
 * The compass mark reuses the SVG from the auth screen's BrandMark so
 * the loading affordance feels like Travelix, not a generic spinner.
 */
export function PullToRefresh({
  onRefresh,
  children,
  className,
  enabled = true,
}: {
  onRefresh: () => Promise<unknown> | void;
  children: ReactNode;
  className?: string;
  enabled?: boolean;
}) {
  const { state, bind } = usePullToRefresh({ onRefresh, enabled });
  const { distance, progress, refreshing } = state;

  // Rotate the compass 360deg over one threshold-length pull. Past
  // the threshold it keeps rotating linearly with `progress` (which
  // grows logarithmically thanks to the rubber-band in the hook).
  const rotate = refreshing ? 360 : progress * 360;

  return (
    <div
      className={cn("relative", className)}
      // In-feed ad banners hide mid-pull: they can't follow the content's
      // transform in lock-step. See InlineAd.
      data-ptr-active={distance > 0 || refreshing ? "" : undefined}
      {...bind}
    >
      {/* Indicator sits absolutely at the top; the content below
          translates down with the pull so the indicator "hangs" from
          the top of the container. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-center"
        style={{
          height: 48,
          transform: `translateY(${Math.max(0, distance - 48)}px)`,
          opacity: refreshing || progress > 0.05 ? 1 : 0,
          transition: refreshing ? "opacity 200ms ease-out" : undefined,
        }}
      >
        <motion.div
          className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-elevated shadow-warm-2"
          style={{ rotate }}
          animate={refreshing ? { rotate: [rotate, rotate + 360] } : undefined}
          transition={
            refreshing
              ? { duration: 0.9, repeat: Infinity, ease: "linear" }
              : { type: "spring", stiffness: 320, damping: 26 }
          }
        >
          <CompassNeedle progress={progress} />
        </motion.div>
      </div>

      <div
        style={{
          transform: `translateY(${distance}px)`,
          transition: refreshing || distance === 0 ? "transform 240ms ease-out" : "none",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function CompassNeedle({ progress }: { progress: number }) {
  // The needle darkens from ink-subtle toward primary as progress
  // approaches the arm point — a visual promise that the release
  // will do something.
  const armed = progress >= 1;
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-line-strong" />
      <path
        d="M12 4 L14.5 13 L12 20 L9.5 13 Z"
        className={armed ? "fill-primary" : "fill-ink"}
      />
    </svg>
  );
}
